import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Popover, TriggerButton, useContextualMenu } from "widgets";

import { loadPlaybooks } from "@/app-utils/playbook.ts";
import { getSavedItemTypeLabel } from "@/app-utils/string.ts";
import type { CoordinateList, InputTableQuerySpec, SavedItem, SavedItemsListing } from "@/graphql.ts";
import { createTableSpecForGroupParent } from "@/redux/modules/table-data.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, PLAYBOOK_CLASS } from "@/settings/settings.ts";
import { ReportPlaybooksMenuDialog } from "@/ui-modules/core/ReportPlaybooksMenuDialog.tsx";

interface ISavedItemReportPlaybooksButtonProps {
  itemsData: SavedItemsListing;
  items: SavedItem[];
  selectedIds: number[];
}

export const SavedItemReportPlaybooksButton: FC<ISavedItemReportPlaybooksButtonProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const meta = props.itemsData;
  const selectedIds = props.selectedIds;
  const type = meta?.type;
  const subType = meta?.subType;

  const [reportPlaybooksAvailable, setReportPlaybooksAvailable] = useState<boolean>(false);
  useEffect(() => {
    void (async () => {
      const playbooks = await dispatch(loadPlaybooks());
      const reportPlaybooks = playbooks.filter((playbook) => playbook.type.class === PLAYBOOK_CLASS.REPORT);
      setReportPlaybooksAvailable(reportPlaybooks.length > 0);
    })();
  }, [type, subType]);

  const coordinateListId = useMemo(() => selectedIds?.[0], [selectedIds]);
  const coordinateList = useMemo(
    () => props.items.find((item) => item.id === coordinateListId) as CoordinateList,
    [coordinateListId, props.items],
  );
  const buttonDisabledMessage = useMemo(() => {
    if (!reportPlaybooksAvailable) {
      return `There are no Playbooks available for producing a report from there.`;
    }
    if (selectedIds.length !== 1) {
      const entityName: string = getSavedItemTypeLabel(meta);
      return `Exactly one ${entityName} must be selected to create a report.`;
    }
    if (subType === ENTITY_TYPE.COORD_LIST && coordinateList?.organism == undefined) {
      return "The organism of the coordinate list is not visible to you";
    }
    return undefined;
  }, [reportPlaybooksAvailable, selectedIds.length, subType, coordinateList?.organism, meta]);

  // build the table query spec corresponding to the saved items selection, in order to have a common report related Playbook input API
  const tableQuerySpec = useMemo(() => {
    if (selectedIds.length && type === ENTITY_TYPE.COORD_LIST && coordinateList?.organism != undefined) {
      // we don't want a list of ENTITY_TYPE.COORD_LIST but rather a specification of ENTITY_TYPE.COORDINATE (only one single list is selectable)
      return {
        id: coordinateList?.id,
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: ENTITY_TYPE.COORD_LIST,
        resultType: ENTITY_TYPE.COORDINATE,
        restrictions: {
          refGenomeId: coordinateList?.organism?.id,
        },
      } satisfies InputTableQuerySpec;
    } else if (subType === ENTITY_TYPE.VCF_VARIANT) {
      const groupId: number | undefined = selectedIds[0];
      return createTableSpecForGroupParent(subType, groupId);
    }
    return undefined;
  }, [type, subType, selectedIds]);

  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        ref={triggerRef}
        icon={"summarize"}
        label={"Reports"}
        id={"button-header-report-playbooks"}
        isDisabled={buttonDisabledMessage}
        tooltip={"Select a Playbook to create a report"}
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <ReportPlaybooksMenuDialog {...menuProps} tableQuerySpec={tableQuerySpec} />
      </Popover>
    </>
  );
};
